// ITER283c-fix — Adapter around <BriefRankedFields />. Reads/writes
// the parent forms' existing state shape:
//   form.preferred_cuisines  (ordered list, index-0 = rank-1)
//   form.target_segments     (ordered list, index-0 = rank-1)
//   form.format              (single-select, legacy — mirrors formats[0])
//   form.preferred_formats   (list, max 2)
//
// Used by BOTH CityIntelligence.js and ExpansionIntelligenceHome.js
// so the Sprint-1c brief redesign lands in both surfaces without
// duplicating the ranked-picker UI.
//
// This adapter is also responsible for DE-DUPLICATION on read so the
// UI never surfaces two rows for the same cuisine (canonical vs
// label vs case/whitespace-variant free-text) or the same segment /
// format id.
import React from "react";
import BriefRankedFields from "./BriefRankedFields";
import {
  CUISINE_CHOICES,
  cuisineIdentityKey,
  normalizeCuisineLabel,
  resolveLabelToClientChip,
} from "../../config/cuisineChoices";

// Given an ENGINE cuisine value (canonical or free-text), return a
// client-label to display. Order:
//   (a) exact-normalised chip label match (e.g. "Indo-Chinese" ↔
//       "indo_chinese" ↔ "INDO CHINESE") → chip.label
//   (b) exact canonical match ("indochinese" → Indo-Chinese chip)
//   (c) UNIQUE prefix match ("continental" or "conti" → Continental
//       European chip) — same resolver used for identity keys, so
//       the display and dedupe paths stay in lock-step
//   (d) raw string (free-text)
function displayLabelForCuisine(engineValue) {
  if (!engineValue) return null;
  const chip = resolveLabelToClientChip(engineValue);
  if (chip) return chip.label;
  return String(engineValue);
}

// De-dupe an engine cuisine list by identity key (canonical > chip
// label > normalised free-text). Preserves order — the FIRST
// occurrence wins. Returns a list of DISPLAY labels ready for the UI.
function dedupeCuisines(engineList) {
  const seen = new Set();
  const out = [];
  for (const item of engineList || []) {
    if (!item) continue;
    const key = cuisineIdentityKey(item);
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(displayLabelForCuisine(item));
  }
  return out;
}

// De-dupe a segment id list case-insensitively. Preserves order.
function dedupeSegments(idList) {
  const seen = new Set();
  const out = [];
  for (const id of idList || []) {
    if (!id) continue;
    const k = String(id).toLowerCase();
    if (seen.has(k)) continue;
    seen.add(k);
    out.push(id);
  }
  return out;
}

// De-dupe format id list case-insensitively. Cap at 2 (schema
// contract). Preserves order.
function dedupeFormats(idList) {
  const seen = new Set();
  const out = [];
  for (const id of idList || []) {
    if (!id) continue;
    const k = String(id).toLowerCase();
    if (seen.has(k)) continue;
    seen.add(k);
    out.push(id);
    if (out.length >= 2) break;
  }
  return out;
}

export default function BriefRankedFieldsAdapter({
  form, setForm, segmentOptions, formatOptions,
}) {
  // Present the parent's ORDERED (and de-duplicated) list as ranked.
  // Cap at MAX_RANK=5 so the UI mirrors what the engine actually
  // reads with weight — anything beyond rank-5 is engine-invisible.
  const rankedCuisines = dedupeCuisines(form.preferred_cuisines || []).slice(0, 5);
  const rankedSegments = dedupeSegments(form.target_segments || []).slice(0, 5);
  const rankedFormats = dedupeFormats(
    (form.preferred_formats && form.preferred_formats.length)
      ? form.preferred_formats
      : (form.format ? [form.format] : []),
  );

  const value = {
    cuisines_ranked: rankedCuisines,
    segments_ranked: rankedSegments,
    formats:         rankedFormats,
  };

  const handleChange = (patch) => {
    setForm((f) => {
      const next = { ...f };
      if ("cuisines_ranked" in patch) {
        // Resolve each client label to its canonical (or pass through
        // free-text unchanged for the backend alias registry).
        // Also collapse case/whitespace duplicates one more time as a
        // safety net.
        const seen = new Set();
        const engineList = [];
        for (const label of patch.cuisines_ranked || []) {
          if (!label) continue;
          const trimmed = String(label).trim();
          if (!trimmed) continue;
          const chip = CUISINE_CHOICES.find(
            (c) => normalizeCuisineLabel(c.label) === normalizeCuisineLabel(trimmed),
          );
          const engineVal = chip && chip.canonical ? chip.canonical : trimmed;
          const key = cuisineIdentityKey(engineVal);
          if (seen.has(key)) continue;
          seen.add(key);
          engineList.push(engineVal);
        }
        next.preferred_cuisines = engineList;
      }
      if ("segments_ranked" in patch) {
        next.target_segments = dedupeSegments(patch.segments_ranked || []);
      }
      if ("formats" in patch) {
        const capped = dedupeFormats(patch.formats || []);
        next.preferred_formats = capped;
        next.format = capped[0] || null;
      }
      return next;
    });
  };

  return (
    <BriefRankedFields
      value={value}
      onChange={handleChange}
      segmentOptions={segmentOptions}
      formatOptions={formatOptions}
    />
  );
}
